"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* POS Section: perfect sales with mobile screenshots & tap animation */
export function BarPubPOS({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Soft blue-purple backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">

          {/* Right Column (Desktop): Text & Content */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 flex flex-col gap-6 lg:order-2"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Perfect sales with <br />
              <span className="x_wd_blue_highlight_bold_05 mt-2 inline-block">
                Point of Sale
              </span>
            </h2>
            <div className="space-y-6">
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
            </div>
          </motion.div>

          {/* Left Column (Desktop): Dynamic Mockups Overlay with Tap Animation */}
          <div className="lg:col-span-6 relative flex flex-col items-center justify-center min-h-[520px] py-10 lg:order-1">
            {/* White/Grey Dome Shape Backdrop Bubble (Same as Bakery) */}
            <div className="absolute top-0 bottom-0 left-[-20%] right-[10%] bg-white/[0.03] rounded-r-[220px] -z-10 pointer-events-none" />

            {/* Back Mockup Phone (Step 1) with Sparkles */}
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -6 }}
              whileInView={{ opacity: 1, y: 0, rotate: -8 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="absolute w-[58%] left-[12%] top-[6%] p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.02] transition-transform duration-300 z-10 x_wd_corner_highlight_03 relative"
            >
              <Image
                src={section.image1}
                width={400}
                height={700}
                sizes="(max-width: 768px) 50vw, 30vw"
                alt="POS Mobile Step 1"
                className="w-full h-auto rounded-[15px] object-cover"
              />
            </motion.div>

            {/* Front Mockup Phone (Step 2) */}
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 6 }}
              whileInView={{ opacity: 1, y: 0, rotate: 4 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="absolute w-[50%] right-[8%] bottom-[6%] p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.02] transition-transform duration-300 z-20 relative"
            >
              <Image
                src={section.image2}
                width={350}
                height={600}
                sizes="(max-width: 768px) 45vw, 25vw"
                alt="POS Mobile Step 2"
                className="w-full h-auto rounded-[15px] object-cover"
              />

              {/* Pulsing Tap Indicator Graphic */}
              {section.tapIcon && (
                <motion.div
                  animate={{ scale: [1, 1.15, 1], y: [0, -2, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                  className="absolute bottom-[20%] left-[20%] w-16 h-16 z-30 pointer-events-none select-none"
                >
                  <Image
                    src={section.tapIcon}
                    fill
                    className="object-contain"
                    alt="Tap Indicator"
                  />
                </motion.div>
              )}
            </motion.div>
          </div>

        </div>

        {/* Persona Speech Bubble Below POS */}
        <div className="s_wd_npc mt-16 flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring" }}
            className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-full py-3 px-6 shadow-xl backdrop-blur-md"
          >
            <div className="w-12 h-12 rounded-full border border-white/20 overflow-hidden relative">
              <Image
                src="/odoo/snippets/s_wd_persona/bee.webp"
                width={48}
                height={48}
                className="object-cover"
                alt="Bee Persona"
              />
            </div>
            <span className="text-white text-xl font-medium italic">
              "All of this from my phone?!"
            </span>
          </motion.div>
        </div>

      </div>
    </section>
  );
}

/* Replenishment Section: stock without the hassle with gear graphics */
export function BarPubReplenishment({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm mint backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="text-center flex flex-col items-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 max-w-4xl`}
          >
            Replenish your{" "}
            <span className="x_wd_green_highlight_02 inline-block mx-1">
              stock
            </span>{" "}
            without the hassle
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-3xl mx-auto flex items-center justify-center mb-10 text-center"
          >
            {/* Spinning background gear left */}
            {section.gearIcon && (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="mt-n3 me-3 d-none d-lg-block flex-shrink-0 brightness-0 invert opacity-100 relative w-[60px] h-[60px]"
              >
                <Image
                  src={section.gearIcon}
                  fill
                  className="object-contain"
                  alt="Left Gear"
                />
              </motion.div>
            )}

            <p className="mb-0 text-gray-300 leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.text }} />

            {/* Spinning background gear right */}
            {section.gearIcon && (
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="self-end ms-3 flex-shrink-0 brightness-0 invert opacity-100 relative w-[40px] h-[40px]"
              >
                <Image
                  src={section.gearIcon}
                  fill
                  className="object-contain"
                  alt="Right Gear"
                />
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Large Mockup Image */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-[1000px] mx-auto p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300"
        >
          <Image
            src={section.image}
            width={1000}
            height={650}
            sizes="(max-width: 1024px) 100vw, 70vw"
            alt="Inventory replenishment list"
            className="w-full h-auto rounded-[18px] object-cover"
          />
        </motion.div>

      </div>
    </section>
  );
}

/* Planning Section: Shift scheduling & scheduling calendar */
export function BarPubPlanning({ section }) {
  return (
    <section className="pb-5 pb-lg-7 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm amber backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container relative z-10 mx-auto px-6 max-w-[1400px]">

        {/* Floating smiling emoji peeking on top of row */}
        {section.emojiIcon && (
          <div className="d-block position-relative mb-n5 z-1 relative w-[80px] h-[80px]">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full h-full"
            >
              <Image
                src={section.emojiIcon}
                fill
                className="object-contain"
                alt="Smiling emoji"
              />
            </motion.div>
          </div>
        )}

        <div className="row gx-5 align-items-center x_wd_shape x_wd_shape_re x_wd_shape_or pt-7 pb-5 relative overflow-visible">
          {/* White/Grey Dome Shape Backdrop Bubble peeking from left */}
          <div className="absolute top-0 bottom-0 left-[-20%] right-[5%] bg-white/[0.03] rounded-r-[220px] -z-10 pointer-events-none" />

          {/* Left Column: Shift Planning Copy */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-lg-6 pb-5 flex flex-col gap-4"
          >
            <h2 className={`${caveat.className} display-2 mb-5 text-white leading-tight font-bold`}>
              <span className="x_wd_yellow_highlight_03 inline-block">
                Smart
              </span>{" "}
              service planning
            </h2>
            <p className="mb-0 text-gray-300 leading-relaxed text-xl" dangerouslySetInnerHTML={{ __html: section.text }} />
          </motion.div>

          {/* Right Column: Shift Planning Mockup Screenshot */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-lg-6 pb-5 flex justify-center lg:justify-end"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full max-w-[580px] relative">
              <Image
                src={section.image}
                width={580}
                height={400}
                alt="Planning shift scheduling"
                className="w-full h-auto rounded-[14px] object-cover"
              />
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
}

/* Project Section: timesheets & checklists detail of the bar */
export function BarPubProject({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Warm rose backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-rose-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Annotated Cursive Arrow and bubble */}
        <div className="mb-12 text-center lg:text-left max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6`}
          >
            Manage every{" "}
            <span className="x_wd_red_highlight_01 mt-2 inline-block">
              detail
            </span>{" "}
            of your bar
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-300 leading-relaxed text-xl"
            dangerouslySetInnerHTML={{ __html: section.text }}
          />
        </div>

        {/* Project Mockup Card and Loop Arrow wrapper */}
        <div className="relative max-w-[850px] mx-auto py-10 flex flex-col items-center">

          {/* Timesheets Mockup Screenshot card */}
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 0 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full p-2 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 z-10 relative"
          >
            <Image
              src={section.image}
              width={850}
              height={550}
              alt="Project tasks timesheets"
              className="w-full h-auto rounded-[18px] object-cover"
            />
          </motion.div>

        </div>

      </div>
    </section>
  );
}
